import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function ShareCoupon() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleShare = async () => {
    if (!email || !/.+@.+\..+/.test(email)) {
      toast.error("Enter a valid email first");
      return;
    }
    setSubmitting(true);
    const shareUrl = window.location.origin;
    const shareData = {
      title: "ARK Furniture — Old wood, new life",
      text: "I just designed a piece with ARK Furniture — take a look and customize your own.",
      url: shareUrl,
    };
    // Only issue the coupon after a real share / clipboard copy succeeds
    let completed = false;
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        completed = true;
      } catch (err) {
        if (err && err.name === "AbortError") {
          toast.message("Share cancelled — no coupon issued");
        } else {
          toast.error("Couldn't open share sheet");
        }
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied — paste it to share with a friend");
        completed = true;
      } catch {
        toast.error("Couldn't copy link to clipboard");
      }
    } else {
      toast.error("Sharing isn't supported on this device");
    }
    if (!completed) {
      setSubmitting(false);
      return;
    }
    try {
      const { data } = await api.post("/coupons", { email });
      setSent(true);
      if (data.already_existing) {
        toast.success("We already had a coupon on file — check your email.");
      } else {
        toast.success(
          "Thanks for sharing! Your 10% off coupon has been sent to your email.",
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't generate coupon");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="share-coupon"
      data-testid="share-coupon-section"
      className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28"
    >
      <div className="border border-[var(--ark-border)] bg-[var(--ark-bone)] px-6 sm:px-12 py-10 sm:py-14 text-center reveal">
        <div className="inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.24em] text-[var(--ark-accent)]">
          <Sparkles size={11} />
          Share & save
        </div>
        <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-3 leading-[1.1]">
          Share ARK Furniture &amp; get 10% off
        </h2>
        <p className="text-sm md:text-base text-[var(--ark-muted)] mt-4 max-w-xl mx-auto leading-relaxed">
          Share our app with a friend and we&apos;ll email you a 10% off coupon
          for your custom piece.
        </p>
        {sent ? (
          <div
            data-testid="share-coupon-success"
            className="mt-8 max-w-md mx-auto border border-black bg-white px-5 py-5"
          >
            <p className="text-sm leading-relaxed">
              Thanks for sharing! Your 10% off coupon has been sent to your
              email. Check your inbox (and spam folder) — it&apos;s good for 30
              days.
            </p>
          </div>
        ) : (
          <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
            <input
              type="email"
              data-testid="share-coupon-email"
              className="ark-input flex-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              autoComplete="email"
            />
            <button
              type="button"
              onClick={handleShare}
              disabled={submitting}
              data-testid="share-coupon-btn"
              className="ark-btn whitespace-nowrap !px-8 !py-4 !tracking-[0.22em] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Working…" : "Share & Get 10% Off"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
